// Helpers to keep every schedule input in Naples wallclock time, regardless
// of the dispatcher's own browser timezone. Two dispatchers (one in Naples,
// one in Europe) must produce the same trip when they type "06:00" for the
// same date.

const NAPLES_TZ = "America/New_York";

/** Returns Naples's UTC offset in MINUTES for the given instant (negative). */
function naplesOffsetMinutesAt(instant) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: NAPLES_TZ,
    timeZoneName: "shortOffset",
  });
  const parts = fmt.formatToParts(instant);
  const raw = parts.find((p) => p.type === "timeZoneName")?.value || "GMT-5";
  const m = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/.exec(raw);
  if (!m) return -300; // safe fallback = EST
  const sign = m[1] === "+" ? 1 : -1;
  return sign * (Number(m[2]) * 60 + Number(m[3] || 0));
}

/**
 * Convert a `<input type="datetime-local">` value (naive "YYYY-MM-DDTHH:mm")
 * to a full UTC ISO string, treating the input as Naples wallclock time.
 * Works during both EDT (-04:00) and EST (-05:00).
 */
export function naplesLocalToUtcISO(local) {
  if (!local) return null;
  const withSeconds = local.length === 16 ? local + ":00" : local;
  const asIfUtc = new Date(withSeconds + "Z");
  if (Number.isNaN(asIfUtc.getTime())) return null;
  const offMin = naplesOffsetMinutesAt(asIfUtc);
  return new Date(asIfUtc.getTime() - offMin * 60_000).toISOString();
}

/**
 * Current instant expressed as a Naples wallclock "YYYY-MM-DDTHH:mm" string —
 * useful as `min` on datetime-local inputs so the dispatcher can't pick a
 * past hour even if their browser thinks it's tomorrow already.
 */
export function nowNaplesLocalString() {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: NAPLES_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}
